import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { AxiosError, AxiosResponse } from 'axios';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Avatar,
  Stack,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  EventNote as AppointmentIcon,
  Medication as MedicationIcon,
  SwapHoriz as HandoverIcon,
  ConfirmationNumber as RequestIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Inventory as StockIcon,
  AccessTime as AttendanceIcon,
} from '@mui/icons-material';
import apiClient from '../services/apiClient';
import { useTenant } from '../contexts/TenantContext';

const reportTypes = [
  {
    id: 'appointments',
    title: 'Medical Appointments Report',
    description: 'Overview of all scheduled medical appointments',
    icon: <AppointmentIcon />,
    color: '#48CAE4',
  },
  {
    id: 'medication_orders',
    title: 'Medication Orders Report',
    description: 'Track all medication orders and their statuses',
    icon: <MedicationIcon />,
    color: '#48CAE4',
  },
  {
    id: 'attendance',
    title: 'Attendance Report',
    description: 'Detailed attendance records of facility staff',
    icon: <AttendanceIcon />,
    color: '#48CAE4',
  },
  {
    id: 'shift_handover',
    title: 'Shift Handover Report',
    description: 'Detailed report of patient care during shift changes',
    icon: <HandoverIcon />,
    color: '#48CAE4',
  },
  {
    id: 'care_requests',
    title: 'Care Request Report',
    description: 'Summary of all care requests and ticket resolutions',
    icon: <RequestIcon />,
    color: '#48CAE4',
  },
  {
    id: 'stock_consumed',
    title: 'Stock Consumed Report',
    description: 'Detailed analysis of medication and supply consumption',
    icon: <StockIcon />,
    color: '#48CAE4',
  },
];

const NO_DATA_MESSAGE_PATTERN = /no\s*data|not\s*found|does\s*not\s*exist|no\s*records|nothing\s*to\s*export|empty/i;

const formatNoDataMessage = (hasDateRange: boolean): string => {
  return hasDateRange
    ? "No records were found for the selected date range."
    : "No records were found.";
};

const extractApiErrorMessage = async (error: unknown): Promise<string | null> => {
  if (!error || typeof error !== 'object') return null;
  const axiosError = error as AxiosError;
  const data = axiosError.response?.data;

  if (data instanceof Blob) {
    try {
      const text = await data.text();
      try {
        const json = JSON.parse(text);
        return json.message || json.error || json.detail || text;
      } catch {
        return text || null;
      }
    } catch {
      return null;
    }
  }

  if (typeof data === 'string') return data;
  if (data && typeof data === 'object') {
    const payload = data as { message?: string; error?: string; detail?: string };
    return payload.message || payload.error || payload.detail || null;
  }

  return axiosError.message || null;
};

const isNoDataExportFailure = (error: unknown, apiMessage: string | null): boolean => {
  const status = (error as AxiosError)?.response?.status;
  if (status === 404 || status === 204) return true;
  if (status === 400 && apiMessage && NO_DATA_MESSAGE_PATTERN.test(apiMessage)) return true;
  if (apiMessage && NO_DATA_MESSAGE_PATTERN.test(apiMessage)) return true;
  if ((error as { isNoData?: boolean })?.isNoData) return true;
  return false;
};

const assertValidExportBlob = async (response: AxiosResponse<Blob>): Promise<void> => {
  const contentType = String(response.headers['content-type'] || '');
  if (contentType.includes('application/json') || contentType.includes('text/plain')) {
    const text = await response.data.text();
    let message = text;
    try {
      const json = JSON.parse(text);
      message = json.message || json.error || json.detail || text;
    } catch {
      // use raw text
    }
    const err = new Error(message) as Error & { isNoData?: boolean };
    err.isNoData = true;
    throw err;
  }
  if (response.data.size === 0) {
    const err = new Error('Empty export response') as Error & { isNoData?: boolean };
    err.isNoData = true;
    throw err;
  }
};

const ReportsPage: React.FC = () => {
  const theme = useTheme();
  const { tenant } = useTenant();
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [selectedReport, setSelectedReport] = useState<string | null>(null);
  const [exportingFormat, setExportingFormat] = useState<'PDF' | 'Excel' | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportInfo, setExportInfo] = useState<string | null>(null);
  const exportErrorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if ((exportError || exportInfo) && exportErrorRef.current) {
      exportErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [exportError, exportInfo]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (exportInfo) {
      timeoutId = setTimeout(() => {
        setExportInfo(null);
      }, 5000);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [exportInfo]);

  const handleExport = async (exportFormat: 'PDF' | 'Excel') => {
    if (!selectedReport) return;

    setExportError(null);
    setExportInfo(null);

    if (fromDate && toDate && toDate < fromDate) {
      setExportError('To Date must be greater than or equal to From Date.');
      return;
    }

    setExportingFormat(exportFormat);
    try {
      const facilityId = tenant?.id || localStorage.getItem('currentTenantId');

      let urlPath = '';
      let fileName = '';

      if (selectedReport === 'appointments' && exportFormat === 'PDF') {
        urlPath = `v1/appointments/export-pdf?facilityId=${facilityId}`;

        if (fromDate) {
          const formattedStartDate = format(fromDate, 'yyyy-MM-dd') + 'T00:00:00';
          urlPath += `&startDate=${formattedStartDate}`;
        }

        if (toDate) {
          const formattedEndDate = format(toDate, 'yyyy-MM-dd') + 'T23:59:59';
          urlPath += `&endDate=${formattedEndDate}`;
        }

        fileName = `Medical_Appointments_Report_${new Date().toLocaleDateString().replace(/\//g, '-')}.pdf`;
      } else if (selectedReport === 'appointments' && exportFormat === 'Excel') {
        urlPath = `v1/appointments/export-excel?facilityId=${facilityId}`;

        if (fromDate) {
          const formattedStartDate = format(fromDate, 'yyyy-MM-dd') + 'T00:00:00';
          urlPath += `&startDate=${formattedStartDate}`;
        }

        if (toDate) {
          const formattedEndDate = format(toDate, 'yyyy-MM-dd') + 'T23:59:59';
          urlPath += `&endDate=${formattedEndDate}`;
        }

        fileName = `Medical_Appointments_Report_${new Date().toLocaleDateString().replace(/\//g, '-')}.xlsx`;
      } else if (selectedReport === 'medication_orders' && exportFormat === 'PDF') {
        urlPath = `v1/medication-orders/export-pdf?facilityId=${facilityId}`;

        if (fromDate) {
          const formattedStartDate = format(fromDate, 'yyyy-MM-dd') + 'T00:00:00';
          urlPath += `&startDate=${formattedStartDate}`;
        }

        if (toDate) {
          const formattedEndDate = format(toDate, 'yyyy-MM-dd') + 'T23:59:59';
          urlPath += `&endDate=${formattedEndDate}`;
        }

        fileName = `Medication_Orders_Report_${new Date().toLocaleDateString().replace(/\//g, '-')}.pdf`;
      } else if (selectedReport === 'medication_orders' && exportFormat === 'Excel') {
        urlPath = `v1/medication-orders/export-excel?facilityId=${facilityId}`;

        if (fromDate) {
          const formattedStartDate = format(fromDate, 'yyyy-MM-dd') + 'T00:00:00';
          urlPath += `&startDate=${formattedStartDate}`;
        }

        if (toDate) {
          const formattedEndDate = format(toDate, 'yyyy-MM-dd') + 'T23:59:59';
          urlPath += `&endDate=${formattedEndDate}`;
        }

        fileName = `Medication_Orders_Report_${new Date().toLocaleDateString().replace(/\//g, '-')}.xlsx`;
      } else if (selectedReport === 'care_requests' && exportFormat === 'PDF') {
        urlPath = `v1/care-requests/export-pdf?facilityId=${facilityId}`;

        if (fromDate) {
          const formattedStartDate = format(fromDate, 'yyyy-MM-dd') + 'T00:00:00';
          urlPath += `&startDate=${formattedStartDate}`;
        }

        if (toDate) {
          const formattedEndDate = format(toDate, 'yyyy-MM-dd') + 'T23:59:59';
          urlPath += `&endDate=${formattedEndDate}`;
        }

        fileName = `Care_Request_Report_${new Date().toLocaleDateString().replace(/\//g, '-')}.pdf`;
      } else if (selectedReport === 'care_requests' && exportFormat === 'Excel') {
        urlPath = `v1/care-requests/export-excel?facilityId=${facilityId}`;

        if (fromDate) {
          const formattedStartDate = format(fromDate, 'yyyy-MM-dd') + 'T00:00:00';
          urlPath += `&startDate=${formattedStartDate}`;
        }

        if (toDate) {
          const formattedEndDate = format(toDate, 'yyyy-MM-dd') + 'T23:59:59';
          urlPath += `&endDate=${formattedEndDate}`;
        }

        fileName = `Care_Request_Report_${new Date().toLocaleDateString().replace(/\//g, '-')}.xlsx`;
      } else if (selectedReport === 'shift_handover' && exportFormat === 'PDF') {
        urlPath = `v1/shift-handover/export-pdf?facilityId=${facilityId}`;

        if (fromDate) {
          const formattedStartDate = format(fromDate, 'yyyy-MM-dd') + 'T00:00:00';
          urlPath += `&startDate=${formattedStartDate}`;
        }

        if (toDate) {
          const formattedEndDate = format(toDate, 'yyyy-MM-dd') + 'T23:59:59';
          urlPath += `&endDate=${formattedEndDate}`;
        }

        fileName = `Shift_Handover_Report_${new Date().toLocaleDateString().replace(/\//g, '-')}.pdf`;
      } else if (selectedReport === 'shift_handover' && exportFormat === 'Excel') {
        urlPath = `v1/shift-handover/export-excel?facilityId=${facilityId}`;

        if (fromDate) {
          const formattedStartDate = format(fromDate, 'yyyy-MM-dd') + 'T00:00:00';
          urlPath += `&startDate=${formattedStartDate}`;
        }

        if (toDate) {
          const formattedEndDate = format(toDate, 'yyyy-MM-dd') + 'T23:59:59';
          urlPath += `&endDate=${formattedEndDate}`;
        }

        fileName = `Shift_Handover_Report_${new Date().toLocaleDateString().replace(/\//g, '-')}.xlsx`;
      } else if (selectedReport === 'stock_consumed' && exportFormat === 'PDF') {
        urlPath = `v1/medication-stock/consumption-report/pdf?facilityId=${facilityId}`;

        if (fromDate) {
          const formattedStartDate = format(fromDate, 'yyyy-MM-dd') + 'T00:00:00';
          urlPath += `&startDate=${formattedStartDate}`;
        }

        if (toDate) {
          const formattedEndDate = format(toDate, 'yyyy-MM-dd') + 'T23:59:59';
          urlPath += `&endDate=${formattedEndDate}`;
        }

        fileName = `Stock_Consumed_Report_${new Date().toLocaleDateString().replace(/\//g, '-')}.pdf`;
      } else if (selectedReport === 'stock_consumed' && exportFormat === 'Excel') {
        urlPath = `v1/medication-stock/consumption-report/excel?facilityId=${facilityId}`;

        if (fromDate) {
          const formattedStartDate = format(fromDate, 'yyyy-MM-dd') + 'T00:00:00';
          urlPath += `&startDate=${formattedStartDate}`;
        }

        if (toDate) {
          const formattedEndDate = format(toDate, 'yyyy-MM-dd') + 'T23:59:59';
          urlPath += `&endDate=${formattedEndDate}`;
        }

        fileName = `Stock_Consumed_Report_${new Date().toLocaleDateString().replace(/\//g, '-')}.xlsx`;
      } else if (selectedReport === 'attendance' && exportFormat === 'PDF') {
        urlPath = `v1/attendance/report/date-range-pdf?facilityId=${facilityId}`;

        if (fromDate) {
          const formattedStartDate = format(fromDate, 'yyyy-MM-dd');
          urlPath += `&startDate=${formattedStartDate}`;
        }

        if (toDate) {
          const formattedEndDate = format(toDate, 'yyyy-MM-dd');
          urlPath += `&endDate=${formattedEndDate}`;
        }

        fileName = `Attendance_Report_${new Date().toLocaleDateString().replace(/\//g, '-')}.pdf`;
      } else if (selectedReport === 'attendance' && exportFormat === 'Excel') {
        urlPath = `v1/attendance/report/date-range?facilityId=${facilityId}`;

        if (fromDate) {
          const formattedStartDate = format(fromDate, 'yyyy-MM-dd');
          urlPath += `&startDate=${formattedStartDate}`;
        }

        if (toDate) {
          const formattedEndDate = format(toDate, 'yyyy-MM-dd');
          urlPath += `&endDate=${formattedEndDate}`;
        }

        fileName = `Attendance_Report_${new Date().toLocaleDateString().replace(/\//g, '-')}.xlsx`;
      }

      if (urlPath) {
        const headers: Record<string, string> = {};
        if (exportFormat === 'Excel') {
          headers['Accept'] = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        } else if (exportFormat === 'PDF') {
          headers['Accept'] = 'application/pdf';
        }

        const response = await apiClient.get(urlPath, {
          responseType: 'blob',
          headers
        });

        await assertValidExportBlob(response);

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        // Placeholder for other report types
        console.log(`Exporting ${selectedReport} as ${exportFormat}...`);
        alert(`${selectedReport} export as ${exportFormat} is currently under development.`);
      }

      // Clear dates after successful export
      setFromDate(null);
      setToDate(null);
    } catch (error) {
      console.error('Export failed:', error);
      const apiMessage = await extractApiErrorMessage(error);
      const status = (error as AxiosError)?.response?.status;
      const hasDateRange = Boolean(fromDate || toDate);
      const noDataFailure = isNoDataExportFailure(error, apiMessage);
      const clientReportError =
        hasDateRange &&
        status !== undefined &&
        status >= 400 &&
        status < 500 &&
        status !== 401 &&
        status !== 403;

      if (noDataFailure || clientReportError) {
        setExportInfo(formatNoDataMessage(hasDateRange));
      } else {
        setExportError('Failed to export report. Please try again later.');
      }
    } finally {
      setExportingFormat(null);
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", pt: 3, pb: 4, px: 0 }}>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span className="material-symbols-rounded" style={{ fontSize: 36, color: '#48CAE4' }}>
            assignment
          </span>
        </Box>
        <Box>
          <Typography variant="h5" fontWeight="700" sx={{ mb: 0.5, color: '#1A2B45' }}>
            Generate New Report
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.85rem" }}>
            Create and export comprehensive clinical, medication, and operational reports
          </Typography>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 4 },
          borderRadius: 4,
          border: '1px solid',
          borderColor: '#E2E8F0',
          bgcolor: '#FFFFFF'
        }}
      >
        {exportError && (
          <Box ref={exportErrorRef} sx={{ mb: 3, width: '100%' }}>
            <Alert
              severity="error"
              onClose={() => setExportError(null)}
              sx={{ borderRadius: 2, width: '100%' }}
            >
              {exportError}
            </Alert>
          </Box>
        )}
        {exportInfo && (
          <Box ref={exportErrorRef} sx={{ mb: 3, width: '100%' }}>
            <Alert
              severity="info"
              onClose={() => setExportInfo(null)}
              sx={{ borderRadius: 2, width: '100%' }}
            >
              {exportInfo}
            </Alert>
          </Box>
        )}

        <Grid container spacing={4}>
          {/* Date Pickers */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#5C6F85' }}>
              From Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                format="dd/MM/yyyy"
                value={fromDate}
                onChange={(newValue) => {
                  setFromDate(newValue);
                  if (newValue && toDate && toDate < newValue) {
                    setToDate(newValue);
                  }
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    placeholder: 'DD/MM/YYYY',
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#F8FAFC'
                      }
                    }
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#5C6F85' }}>
              To Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                format="dd/MM/yyyy"
                value={toDate}
                minDate={fromDate || undefined}
                onChange={(newValue) => {
                  if (newValue && fromDate && newValue < fromDate) {
                    setToDate(fromDate);
                  } else {
                    setToDate(newValue);
                  }
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    placeholder: 'DD/MM/YYYY',
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#F8FAFC'
                      }
                    }
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          {/* Report Type Selector */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#5C6F85' }}>
              Select Report Type
            </Typography>
            <Grid container spacing={2}>
              {reportTypes.map((report) => (
                <Grid item xs={12} md={6} key={report.id}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: selectedReport === report.id ? report.color : '#E2E8F0',
                      bgcolor: selectedReport === report.id ? alpha(report.color, 0.04) : '#FFFFFF',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: report.color,
                        bgcolor: alpha(report.color, 0.02),
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                      },
                      boxShadow: selectedReport === report.id ? `0 0 0 1px ${report.color}` : 'none'
                    }}
                  >
                    <CardActionArea onClick={() => setSelectedReport(report.id)} sx={{ p: 2.5 }}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar
                          sx={{
                            bgcolor: report.color,
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                          }}
                        >
                          {report.icon}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="normal" sx={{ color: '#1A2B45' }}>
                            {report.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#5C6F85' }}>
                            {report.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Export Buttons */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#5C6F85' }}>
              Export Format
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={exportingFormat === 'PDF' ? <CircularProgress size={16} color="inherit" /> : <PdfIcon />}
                disabled={!selectedReport || exportingFormat === 'PDF'}
                onClick={() => handleExport('PDF')}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.2,
                  textTransform: 'none',
                  fontWeight: 600,
                  color: '#0077B6',
                  borderColor: '#0077B6',
                  '&:hover': {
                    borderColor: '#005F92',
                    bgcolor: 'rgba(0, 119, 182, 0.04)',
                  },
                  '&.Mui-disabled': {
                    borderColor: '#E2E8F0',
                  }
                }}
              >
                {exportingFormat === 'PDF' ? 'Exporting...' : 'Export as PDF'}
              </Button>
              <Button
                variant="outlined"
                startIcon={exportingFormat === 'Excel' ? <CircularProgress size={16} color="inherit" /> : <ExcelIcon />}
                disabled={!selectedReport || exportingFormat === 'Excel'}
                onClick={() => handleExport('Excel')}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.2,
                  textTransform: 'none',
                  fontWeight: 600,
                  color: '#0077B6',
                  borderColor: '#0077B6',
                  '&:hover': {
                    borderColor: '#005F92',
                    bgcolor: 'rgba(0, 119, 182, 0.04)',
                  },
                  '&.Mui-disabled': {
                    borderColor: '#E2E8F0',
                  }
                }}
              >
                {exportingFormat === 'Excel' ? 'Exporting...' : 'Export as Excel'}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default ReportsPage;
